import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import axios from '../../../../axiosConfig';
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import PriceDataBox from './PriceDataBox';
import styles from '../../MicroProbe/Companies/FinancialData.module.css'; 
import CustomDropdown from './CustomDropdown';


ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend);

function PriceDataIntraday({ inputData }) {
  const [financialData, setFinancialData] = useState({});
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState({});
  const [currentView, setCurrentView] = useState('');
  const [metricSelection, setMetricSelection] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [to_symbol, setToSymbol] = useState("");
  const [from_symbol, setFromSymbol] = useState("");
  const [interval, setInterval] = useState('');
  const resetState = () => {
    setFinancialData({});
    setChartData({});
    setCurrentView('');
    setMetricSelection('');
    setStartDate('');
    setEndDate('');
    setLoading(false);
  };

  
  const metricOptions = {
    TIME_SERIES_DAILY_ADJUSTED: [
      '4. close',
      '1. open',
      '2. high',
      '3. low',
    ]
  };

  const handleFromForeignExchangeSelect = async (foreignExchangeName) => {
    try {
      const response = await axios.get(`/api/foreignexchange/name/${foreignExchangeName}`);
      setFromSymbol(response.data.code);
      resetState();
    } catch (error) {
      console.error('Error fetching currency data:', error);
    }
  };

  const handleForeignExchangeSelect = async (foreignExchangeName) => {
    try {
      const response = await axios.get(`/api/foreignexchange/name/${foreignExchangeName}`);
      setToSymbol(response.data.code);
      resetState();
    } catch (error) {
      console.error('Error fetching currency data:', error);
    }
  };



  const handleButtonClick = async (functionType) => {
    setLoading(true);
    setCurrentView(functionType);
    setMetricSelection('');
    try {
      let response = await axios.get(`get_fx_data_intraday/${from_symbol}/${to_symbol}/${interval}/`);

      setFinancialData(response.data);
    } catch (error) {
      console.error(`Error fetching data: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fxDataKey = Object.keys(financialData).find(k => k.startsWith("Time Series FX"));
    let reports = [];
  
    if (fxDataKey && financialData[fxDataKey]) {
      reports = Object.entries(financialData[fxDataKey]).map(([date, data]) => ({
        ...data,
        fiscalDateEnding: date
      }));
    }
  
    if (metricSelection && reports.length > 0) {
      reports = reports.filter(report => {
        const reportDate = new Date(report.fiscalDateEnding);
        return (!startDate || reportDate >= new Date(startDate)) && (!endDate || reportDate <= new Date(endDate));
      });
  
      const labels = reports.map(report => report.fiscalDateEnding).reverse();
      const dataPoints = reports.map(report => parseFloat(report[metricSelection]) || 0).reverse();
  
      const datasets = {
        label: metricSelection.replace(/^\d+\.\s/, ''),
        data: dataPoints,
        fill: false,
        backgroundColor: 'rgb(75, 192, 192)',
        borderColor: 'rgb(75, 192, 192)',
        pointRadius: 0
      };
  
      setChartData({
        labels,
        datasets: [datasets]
      });
    }
  }, [metricSelection, financialData, startDate, endDate]);
  

  const renderChartOrNote = () => {
    if (typeof chartData.datasets[0].data[0] === 'string') {
      return <pre>{JSON.stringify(chartData, null, 2)}</pre>; 
    } else {
      return (
        <div className={styles.chartContainer}>
          <div className={styles.scrollableChart}>
            <div className={styles.chartContent}>
              <Line 
                data={chartData} 
                options={{ 
                  responsive: true,
                  scales: {
                    x: {
                      ticks: {
                        maxRotation: 0, 
                        autoSkipPadding: 20 
                      }
                    }
                  },
                  plugins: {
                    tooltip: {
                      bodyFont: {
                        size: window.innerWidth < 768 ? 12 : 14 // Adjust font size for mobile
                      },
                      titleFont: {
                        size: window.innerWidth < 768 ? 12 : 14 // Adjust title font size for mobile
                      }
                    }
                  }
                }} 
              />
            </div>
          </div>
        </div>
      );
    }
  };

 
  const handleMetricChange = (selectedValue) => setMetricSelection(selectedValue);

  return (
    <div className={styles.container}>
      <h3>Intraday Metrics</h3>  

      <div className={styles.selectionContainer}>
        <label className={styles.label}>From: </label>
        <PriceDataBox onForeignExchangeSelect={handleFromForeignExchangeSelect} />
      </div>

      <div className={styles.selectionContainer}>
        <label className={styles.label}>To: </label>
        <PriceDataBox onForeignExchangeSelect={handleForeignExchangeSelect} />
      </div>

      {from_symbol && to_symbol && (
        <CustomDropdown 
        onSelect={(selectedValue) => {
            setInterval(selectedValue);
            resetState(); // fully clear on interval change
          }}
        placeholder="Select Interval" 
        options={['1min', '5min', '15min', '30min', '60min'].map(opt => ({
            value: opt,
            label: opt
        }))}
        />
        )}
  

      {(!from_symbol || !to_symbol) && (
        <p className={styles.helperText}>Please select currency pair</p>
      )}

      {from_symbol && to_symbol && interval &&(
        <button 
          onClick={() => handleButtonClick('TIME_SERIES_DAILY_ADJUSTED')}
          className={styles.button}
        >
          Get FX Data
        </button>
      )}


      {currentView && (
        <div className={styles.chartControls}>


          <CustomDropdown 
            onSelect={handleMetricChange} 
            placeholder="Select a Metric" 
            options={metricOptions[currentView] ? metricOptions[currentView].map(option => ({
              value: option,
              label: option.replace(/^\d+\.\s/, '')
            })) : []} 
            resetTrigger={currentView}
          />



          <div className={styles.dateInputs}>
            <div>
              <label>From:</label>
              <input type="date" onChange={event => setStartDate(event.target.value)} value={startDate} />
            </div>
            <div>
              <label>To:</label>
              <input type="date" onChange={event => setEndDate(event.target.value)} value={endDate} />
            </div>
          </div>
        </div>
      )}
      {loading ? <p>Loading...</p> : (metricSelection && Object.keys(chartData).length > 0 ? renderChartOrNote() : null)}
    </div>
  );
}

export default PriceDataIntraday;
